import { apiFetch, type ApiFetchOptions, type ApiFetchResult, type Portal } from "@/lib/apiClient";
import { toContractResult, type ContractResult } from "./contractErrors";

/**
 * True when VITE_USE_MOCKS=true in a dev/test build. Read per call (not at
 * module load) so tests can flip it with vi.stubEnv. Never true in a production
 * build — `import.meta.env.PROD` is a compile-time constant, so the mock module
 * below is dropped from the bundle entirely.
 */
export function mocksEnabled(): boolean {
  return !import.meta.env.PROD && import.meta.env.VITE_USE_MOCKS === "true";
}

type Method = "GET" | "POST" | "PUT" | "DELETE";

interface RequestOptions {
  method: Method;
  body?: object;
  /** Which portal's bearer token to attach. Omit for the public endpoints. */
  portal?: Extract<Portal, "admin" | "clinician" | "patient">;
  headers?: Record<string, string>;
  /** Overrides apiFetch's default timeout, for calls that legitimately take longer (e.g. ones that send email). */
  timeoutMs?: number;
}

/**
 * apiFetch, or — with mocks on — the same request answered by mockServer.ts in
 * the same ApiFetchResult shape. For API modules that keep their own result
 * shape (e.g. the patient records/notifications calls) instead of ContractResult.
 */
export async function mockAwareFetch<T>(path: string, options: ApiFetchOptions & { method: Method }): Promise<ApiFetchResult<T>> {
  if (mocksEnabled()) {
    const { handleMockRequest } = await import("./mockServer");
    const body = typeof options.body === "string" ? (JSON.parse(options.body) as object) : undefined;
    return handleMockRequest<T>(options.method, path, body, options.headers);
  }
  return apiFetch<T>(path, options);
}

/**
 * Single seam between the contract API functions and the network. With mocks off
 * this is plain apiFetch (token attachment, refresh-on-401, timeout); with mocks
 * on, the same request is answered by mockServer.ts in the same ApiFetchResult
 * shape — so both paths go through the one error parser in contractErrors.ts.
 */
export async function contractRequest<T>(path: string, options: RequestOptions): Promise<ContractResult<T>> {
  const result = await mockAwareFetch<T>(path, {
    method: options.method,
    body: options.body ? JSON.stringify(options.body) : undefined,
    portal: options.portal,
    skipAuth: !options.portal,
    headers: options.headers,
    timeoutMs: options.timeoutMs,
  });
  return toContractResult(result);
}
